import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import { ConfigProps, change, formValueSelector } from 'redux-form'
import { useLocation, useParams, useRouteMatch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CountryCode } from '@carsys/enums/country-code'
import TabbedForm from '@cbgms/components/layout/TabbedForm'
import TabItem from '@cbgms/components/form/Tabs/TabItem'
import { IConcept, IShopConcept } from '@cbgms/api/modules/concepts'
import { IWithDataProps } from '@cbgms/base/http/WithData'
import { ISupportShop } from '@cbgms/api/modules/shops'
import { EmptyDateTime } from '@cbgms/base/utils/datetime'
import ExternalSystems from './ExternalSystems/Overview'
import { GETLocalisationSettingsResponse, ILocalizationSettings } from '@cbgms/api/modules/settings'
import { createShop, updateShop } from '../state/actions'
import { IRootReducerState } from 'state/reducers'
import { createShopConcept, updateShopConcept } from 'app-support/concepts/state/actions'

import Localization from './Localization'
import AddShopToCRM from './AddShopToCRM'
import Address from './Address'
import Financial from './Financial'
import General from './General'
import Modules from './Modules'
import Themes from './Themes'
import Employees from './Employees'
import Concepts from './Concepts'
import { useFeature } from '@cbgms/base/feature-flags'
import { useIsPortal } from '../hooks/use-is-portal'

interface IShopFormProps {
  updateShopLocalizationSettings: (shopID: string, data: Partial<ILocalizationSettings>) => Promise<GETLocalisationSettingsResponse>
  postShopToCRM?: (shopID: string) => Promise<{}>
  onConceptSubmit?: (conceptUUID: string) => (...input: any) => Promise<void>
  onConceptAdd?: (conceptUUID: string) => (...input: any) => Promise<void>
  onAddShopToCRM?: (shopID: string) => Promise<void>
  createShop?: typeof createShop
  updateShop?: typeof updateShop
  updateShopConcept?: typeof updateShopConcept
  createShopConcept?: typeof createShopConcept
}
interface LoaderResult {
  concepts: IConcept[]
  shop: ISupportShop
  shopConcepts: IShopConcept[]
  takeoverURL: string
}

export interface ShopFormProps extends IShopFormProps, Pick<IWithDataProps<LoaderResult>, 'result'>, ConfigProps<IFormValues, Dispatch> {
  isMaterialTab?: (hash: string) => boolean
}

export interface IFormValues {
  Shop: ISupportShop
  ShopConcepts: IShopConcept[]
  ShopLocalizationSettings: ILocalizationSettings
}

const queryLocalizationSettingsByCountry = (country: CountryCode) => {
  switch (country) {
    case CountryCode.BE: {
      return {
        Locale: 'fr-BE',
        Timezone: 'Europe/Brussels',
        TimeNotation: '24hour'
      }
    }
    case CountryCode.NL: {
      return {
        Locale: 'nl-NL',
        Timezone: 'Europe/Amsterdam',
        TimeNotation: '24hour'
      }
    }
    case CountryCode.GB: {
      return {
        Locale: 'en-GB',
        Timezone: 'Europe/London',
        TimeNotation: '24hour'
      }
    }
    case CountryCode.FR: {
      return {
        Locale: 'fr-FR',
        Timezone: 'Europe/Paris',
        TimeNotation: '24hour'
      }
    }
    case CountryCode.DE: {
      return {
        Locale: 'de-DE',
        Timezone: 'Europe/Berlin',
        TimeNotation: '24hour'
      }
    }
    case CountryCode.IT: {
      return {
        Locale: 'it-IT',
        Timezone: 'Europe/Rome',
        TimeNotation: '24hour'
      }
    }
    case CountryCode.CZ: {
      return {
        Locale: 'cs-CZ',
        Timezone: 'Europe/Prague',
        TimeNotation: '24hour'
      }
    }
    case CountryCode.ES: {
      return {
        Locale: 'es-ES',
        Timezone: 'Europe/Madrid',
        TimeNotation: '24hour'
      }
    }
    case CountryCode.LU: {
      return {
        Locale: 'fr-LU',
        Timezone: 'Europe/Amsterdam',
        TimeNotation: '24hour'
      }
    }
    default: {
      return {
        Locale: '',
        Timezone: '',
        TimeNotation: ''
      }
    }
  }
}

// determin if this is a material tab
const defaultIsMaterialTab = (hash: string) => hash?.indexOf('address') !== -1 || hash?.indexOf('general') !== -1 || hash.length === 0

const ShopForm: React.FC<ShopFormProps> = props => {
  const hasAdditionalLanguageInShop = useFeature('additional-language-in-shop')
  const { result, onConceptSubmit, onConceptAdd, onAddShopToCRM, isMaterialTab } = props
  const { companyUUID, shopID } = useParams<{ companyUUID: string; shopID?: string }>()
  const { t } = useTranslation()
  const { hash } = useLocation()
  const { url } = useRouteMatch()
  const dispatch = useDispatch()
  const isPortal = useIsPortal()

  const useMaterial = (isMaterialTab || defaultIsMaterialTab)(hash)

  const isAddedToCRM = EmptyDateTime !== result?.shop?.AddedToCRMAt
  const selector = formValueSelector(props.form)
  const { country, locale } = useSelector((state: IRootReducerState) => ({
    country: selector(state, `Shop.VisitorCountry`),
    locale: selector(state, `ShopLocalizationSettings.Locale`) || ''
  }))

  useEffect(() => {
    if (!country) return
    if (hasAdditionalLanguageInShop && isPortal) {
      const defaultsLocalizationSettings = queryLocalizationSettingsByCountry(country)

      // If Locale is empty, set a default once
      if (!locale || String(locale).trim() === '') {
        dispatch(change(props.form, 'ShopLocalizationSettings', defaultsLocalizationSettings))
      }
    } else if (!locale.endsWith(country)) {
      // we only want to reset the current localization settings if they are not valid for that country

      const localizationSettings = queryLocalizationSettingsByCountry(country)
      dispatch(change(props.form, `ShopLocalizationSettings`, localizationSettings))
    }
  }, [hasAdditionalLanguageInShop, isPortal, country, locale])

  return (
    <TabbedForm {...props} useMaterial={useMaterial} className='shopForm'>
      <TabItem title={t('shop.general_tab')} name='general'>
        <General takeoverURL={result?.takeoverURL} shopID={shopID} companyUUID={companyUUID} country={country} />
      </TabItem>
      <TabItem title={t('shop.address_tab')} name='address'>
        <Address form={props.form} />
      </TabItem>
      <TabItem title={t('shop.localization')} name='localization'>
        <Localization country={country} />
      </TabItem>
      <TabItem title={t('shop.financial_tab')} name='financial'>
        <Financial country={country} />
      </TabItem>
      <TabItem title={t('shop.shop_employees_tab')} name='employees'>
        <Employees currentUrl={url} companyUUID={companyUUID} shopID={shopID} />
      </TabItem>
      <TabItem title={t('shop.modules_tab')} name='modules'>
        <Modules form={props.form} />
      </TabItem>
      <TabItem title={t('shop.theme_tab')} name='theme'>
        <Themes />
      </TabItem>
      {shopID && shopID !== 'new' && (
        <TabItem title={t('shop.external_systems')} name='externalsystems'>
          <ExternalSystems />
        </TabItem>
      )}
      <TabItem title={t('Shop.ConceptsTab')} name='concepts' hide={!result?.concepts}>
        {result?.concepts ? (
          isAddedToCRM && onConceptAdd && onConceptSubmit ? (
            <Concepts
              concepts={result.concepts}
              shopUUID={result.shop.UUID}
              shopID={result.shop.ID}
              companyUUID={companyUUID}
              onConceptAdd={onConceptAdd}
              onSubmit={onConceptSubmit}
            />
          ) : (
            onAddShopToCRM && <AddShopToCRM shopID={result!.shop.ID} onAddShopToCRM={onAddShopToCRM} />
          )
        ) : null}
      </TabItem>
    </TabbedForm>
  )
}
ShopForm.displayName = 'ShopForm'
export default ShopForm
